import { useState } from 'react'
import { motion } from 'framer-motion'
import { Badge } from './ui'
import { useT } from '../lib/i18n'
import type { ChatLocale } from '../lib/locales'

const REVEAL_BASE_DELAY = 0.3
const REVEAL_STEP = 0.7

interface BubbleProps {
  bubble: ChatLocale
  index: number
  noSourceLabel: string
}

function Bubble({ bubble, index, noSourceLabel }: BubbleProps) {
  const isUser = bubble.who === 'user'
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: REVEAL_BASE_DELAY + index * REVEAL_STEP, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[78%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div
          className={`mono text-[length:var(--text-micro)] uppercase tracking-widest ${
            isUser ? 'text-[color:var(--color-orion-haze)]' : 'text-[color:var(--color-orion-amber)]'
          }`}
        >
          {bubble.meta}
        </div>
        <div
          className={`rounded-[var(--radius-lg)] px-4 py-3 leading-relaxed ${
            isUser
              ? 'bg-[color:var(--color-orion-surface)]/70 text-[color:var(--color-orion-text)] rounded-tr-[6px]'
              : 'bg-[color:var(--color-orion-deep)]/80 border-faint text-[color:var(--color-orion-text)] rounded-tl-[6px]'
          }`}
          style={{ fontSize: 'var(--text-body)' }}
        >
          {bubble.text}
        </div>
        {!isUser && <Badge tone="neutral">{noSourceLabel}</Badge>}
      </div>
    </motion.div>
  )
}

export function L1Demo() {
  const [runKey, setRunKey] = useState(0)
  const t = useT()
  const l1 = t.l1

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-[color:var(--color-orion-haze)]" />
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-haze)]">
            {l1.header}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="neutral">{l1.noDataBadge}</Badge>
          <button
            type="button"
            onClick={() => setRunKey((k) => k + 1)}
            className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-2.5 py-1 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:bg-[color:var(--color-orion-amber)]/15 hover:border-[color:var(--color-orion-amber)]/60 hover:text-[color:var(--color-orion-amber)] transition-colors"
          >
            {l1.rerunButton}
          </button>
        </div>
      </div>

      <div
        key={runKey}
        data-demo-target="l1-chat"
        className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-6 space-y-4"
      >
        {l1.chat.map((bubble, i) => (
          <Bubble
            key={`${runKey}-${i}`}
            bubble={bubble}
            index={i}
            noSourceLabel={l1.bubbleNoSource}
          />
        ))}

        <motion.div
          key={`input-${runKey}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: REVEAL_BASE_DELAY + l1.chat.length * REVEAL_STEP }}
          className="flex items-center gap-2 pt-3 border-t border-[color:var(--color-orion-surface)]/50"
        >
          <div className="flex-1 px-3 py-2 rounded-[var(--radius-md)] border-faint text-[length:var(--text-small)] text-[color:var(--color-orion-haze)]">
            <span className="mono">{l1.inputPlaceholder}</span>
          </div>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] uppercase tracking-widest">
            {l1.protocolTag}
          </span>
        </motion.div>
      </div>
    </div>
  )
}
